import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { FileText, Clock, CheckCircle, Loader2, Copy, ChevronDown, ChevronUp, RefreshCw, MessageSquare, Send, Lightbulb, ShieldCheck, Download, Play, Image, Mic, X, Sparkles, Code2, Eye, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ChatInterface } from '@/components/ChatInterface';
import { CanvasPreview } from '@/components/CanvasPreview';
import { TommyBackground } from '@/components/TommyBackground';
import { useAuth } from '@/context/AuthContext';
import { useSound } from '@/context/SoundContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floating particles component
function FloatingParticles() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: i % 3 === 0 ? '#14b8a6' : i % 3 === 1 ? '#22c55e' : '#2563eb',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.1, 0.5, 0.1],
          }}
          transition={{
            duration: 4 + Math.random() * 3,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

export default function MyOrders() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { playSound } = useSound();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [revisionFeedback, setRevisionFeedback] = useState({});
  const [requestingRevision, setRequestingRevision] = useState(null);
  const [chatOpen, setChatOpen] = useState(null);
  const [canvasContent, setCanvasContent] = useState(null); // For Canvas/Artifacts preview
  const [canvasFullscreen, setCanvasFullscreen] = useState(false);

  // Block keyboard shortcuts for copying in protected content
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if the target is within a protected content area
      const isProtected = e.target.closest('[data-protected="true"]');
      if (isProtected) {
        // Block Ctrl+C, Ctrl+A, Ctrl+X, Ctrl+P
        if (e.ctrlKey && ['c', 'a', 'x', 'p'].includes(e.key.toLowerCase())) {
          e.preventDefault();
        }
        // Block Cmd+C, Cmd+A, Cmd+X, Cmd+P (Mac)
        if (e.metaKey && ['c', 'a', 'x', 'p'].includes(e.key.toLowerCase())) {
          e.preventDefault();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth?mode=login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/house-agents/my-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data.orders || []);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, token, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const downloadMedia = (base64Data, mimeType, filename) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${filename}`);
    } catch (error) {
      toast.error('Download failed');
      console.error('Download error:', error);
    }
  };

  const handleRequestRevision = async (orderId) => {
    const feedback = revisionFeedback[orderId];
    if (!feedback?.trim()) {
      toast.error('Please provide feedback for the revision');
      return;
    }

    setRequestingRevision(orderId);
    try {
      const response = await axios.post(
        `${API}/house-agents/request-revision/${orderId}?feedback=${encodeURIComponent(feedback)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the order in the list with new output
      setOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              work_output: response.data.work_output,
              revision_count: response.data.revision_number
            }
          : order
      ));
      
      setRevisionFeedback(prev => ({ ...prev, [orderId]: '' }));
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to request revision';
      toast.error(errorMsg);
    } finally {
      setRequestingRevision(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'pending_payment':
      case 'paid':
        return <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-zinc-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'pending_payment':
        return 'Pending Payment';
      case 'paid':
        return 'Processing';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 relative">
      {/* Tommy's animated background - Time-of-day aware! */}
      <TommyBackground useTimeOfDay={true} opacity={0.12} />
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">My Orders</h1>
          <p className="text-zinc-400">Your House Agent work deliveries</p>
        </motion.div>

        {orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No orders yet</h2>
            <p className="text-zinc-400 mb-6">Hire a House Agent to get instant AI work</p>
            <Button onClick={() => navigate('/agents')} className="bg-cyan-500 hover:bg-cyan-600">
              Browse Agents
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, idx) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-zinc-900/50 border border-white/10 rounded-xl overflow-hidden"
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(order.status)}
                      <div>
                        <h3 className="text-white font-medium">{order.agent_name}</h3>
                        <p className="text-zinc-500 text-sm">
                          {new Date(order.created_at).toLocaleDateString()} • ${order.amount}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm px-2 py-1 rounded ${
                        order.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {getStatusText(order.status)}
                      </span>
                      {expandedOrder === order.id ? (
                        <ChevronUp className="w-5 h-5 text-zinc-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-zinc-400" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedOrder === order.id && order.work_output && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="border-t border-white/10 p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-400 text-sm">Delivered Work</span>
                        {order.output_type === 'video' && (
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Play className="w-3 h-3" />
                            Video
                          </span>
                        )}
                        {order.output_type === 'image' && (
                          <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Image className="w-3 h-3" />
                            Image
                          </span>
                        )}
                        {order.output_type === 'audio' && (
                          <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded flex items-center gap-1">
                            <Mic className="w-3 h-3" />
                            Audio
                          </span>
                        )}
                        {order.revision_count > 0 && (
                          <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">
                            Revision {order.revision_count}
                          </span>
                        )}
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" />
                          Protected
                        </span>
                      </div>
                      
                      {/* Chat and Action Buttons */}
                      <div className="flex gap-2">
                        {/* Chat Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChatOpen(chatOpen === order.id ? null : order.id);
                          }}
                          className={`${chatOpen === order.id ? 'bg-teal-500/20 border-teal-500/50 text-teal-400' : 'border-zinc-700 text-zinc-400 hover:text-teal-400 hover:border-teal-500/50'}`}
                          data-testid={`chat-btn-${order.id}`}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Chat
                        </Button>
                        
                        {/* Show appropriate action button based on output type */}
                        {order.output_type === 'video' && order.work_output?.data ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadMedia(order.work_output.data, 'video/mp4', `tripletc-video-${order.id.slice(0,8)}.mp4`);
                            }}
                            className="text-purple-400 hover:text-purple-300"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download Video
                          </Button>
                        ) : order.output_type === 'image' && order.work_output?.images ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              order.work_output.images.forEach((img, i) => {
                                downloadMedia(img, 'image/png', `tripletc-image-${order.id.slice(0,8)}-${i+1}.png`);
                              });
                            }}
                            className="text-pink-400 hover:text-pink-300"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download {order.work_output.images?.length > 1 ? `${order.work_output.images.length} Images` : 'Image'}
                          </Button>
                        ) : order.output_type === 'audio' && order.work_output?.data ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadMedia(order.work_output.data, 'audio/mpeg', `tripletc-audio-${order.id.slice(0,8)}.mp3`);
                            }}
                            className="text-orange-400 hover:text-orange-300"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download Audio
                          </Button>
                        ) : (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(typeof order.work_output === 'string' ? order.work_output : JSON.stringify(order.work_output));
                              }}
                              className="text-zinc-400 hover:text-white"
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCanvasContent({
                                  content: typeof order.work_output === 'string' ? order.work_output : JSON.stringify(order.work_output, null, 2),
                                  title: order.agent_name + ' Output',
                                  type: 'auto'
                                });
                                playSound?.('pop');
                              }}
                              className="text-teal-400 hover:text-teal-300"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Canvas
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Render based on output type */}
                    {order.output_type === 'video' && order.work_output?.data ? (
                      <div className="space-y-3">
                        <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
                          <video 
                            controls 
                            className="w-full max-h-80"
                            src={`data:video/mp4;base64,${order.work_output.data}`}
                          >
                            Your browser does not support video playback.
                          </video>
                        </div>
                        <div className="text-xs text-zinc-500 flex gap-4">
                          <span>Duration: {order.work_output.settings?.duration}s</span>
                          <span>Resolution: {order.work_output.settings?.size}</span>
                          <span>Model: {order.work_output.settings?.model}</span>
                        </div>
                        {order.work_output.prompt_used && (
                          <div className="bg-zinc-800/30 rounded-lg p-3 mt-2">
                            <p className="text-xs text-zinc-500 mb-1">Prompt Used:</p>
                            <p className="text-xs text-zinc-400">{order.work_output.prompt_used}</p>
                          </div>
                        )}
                      </div>
                    ) : order.output_type === 'image' && order.work_output?.images ? (
                      <div className="space-y-3">
                        <div className={`grid gap-3 ${order.work_output.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                          {order.work_output.images.map((img, idx) => (
                            <div key={idx} className="bg-zinc-800/50 rounded-lg overflow-hidden">
                              <img 
                                src={`data:image/png;base64,${img}`}
                                alt={`Generated image ${idx + 1}`}
                                className="w-full h-auto"
                                onContextMenu={(e) => e.preventDefault()}
                                draggable={false}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-zinc-500">
                          <span>{order.work_output.count} image(s) generated</span>
                          <span className="mx-2">•</span>
                          <span>Model: {order.work_output.settings?.model}</span>
                        </div>
                        {order.work_output.prompt_used && (
                          <div className="bg-zinc-800/30 rounded-lg p-3">
                            <p className="text-xs text-zinc-500 mb-1">Prompt Used:</p>
                            <p className="text-xs text-zinc-400">{order.work_output.prompt_used}</p>
                          </div>
                        )}
                      </div>
                    ) : order.output_type === 'audio' && order.work_output?.data ? (
                      <div className="space-y-3">
                        <div className="bg-zinc-800/50 rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                              <Mic className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p className="text-white font-medium">AI Generated Voiceover</p>
                              <p className="text-xs text-zinc-400">
                                {order.work_output.settings?.word_count} words • ~{order.work_output.settings?.estimated_duration_seconds}s
                              </p>
                            </div>
                          </div>
                          <audio 
                            controls 
                            className="w-full"
                            src={`data:audio/mpeg;base64,${order.work_output.data}`}
                          >
                            Your browser does not support audio playback.
                          </audio>
                        </div>
                        <div className="text-xs text-zinc-500">
                          <span>Voice Model: {order.work_output.settings?.model}</span>
                        </div>
                        {order.work_output.script_used && (
                          <div className="bg-zinc-800/30 rounded-lg p-3">
                            <p className="text-xs text-zinc-500 mb-1">Script Used:</p>
                            <p className="text-xs text-zinc-400">{order.work_output.script_used}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Text content - Protected area */
                      <div 
                        className="bg-zinc-800/50 rounded-lg p-4 max-h-64 overflow-y-auto relative"
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                        data-protected="true"
                      >
                        <pre 
                          className="text-zinc-300 whitespace-pre-wrap text-sm leading-relaxed font-sans"
                          onCopy={(e) => e.preventDefault()}
                          onCut={(e) => e.preventDefault()}
                          onDragStart={(e) => e.preventDefault()}
                        >
                          {typeof order.work_output === 'string' ? order.work_output : JSON.stringify(order.work_output, null, 2)}
                        </pre>
                        <div className="absolute bottom-2 right-2 text-xs text-zinc-600/50 pointer-events-none">
                          TripleTC Protected Content
                        </div>
                      </div>
                    )}

                    {/* Revision Request Section */}
                    {order.status === 'completed' && (order.revision_count || 0) < 2 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-center gap-2 mb-3">
                          <RefreshCw className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm text-zinc-300">Request Revision</span>
                          <span className="text-xs text-zinc-500">
                            ({2 - (order.revision_count || 0)} free revision{2 - (order.revision_count || 0) !== 1 ? 's' : ''} remaining)
                          </span>
                        </div>
                        
                        {/* Revision Suggestions */}
                        <div className="mb-3 p-3 bg-zinc-800/30 rounded-lg border border-white/5">
                          <p className="text-xs text-zinc-500 flex items-center gap-1.5 mb-2">
                            <Lightbulb className="w-3 h-3 text-yellow-400" />
                            <span>Suggestion prompts:</span>
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {[
                              "Make it more formal",
                              "Add statistics/data",
                              "Shorten to key points",
                              "More conversational tone",
                              "Add specific examples"
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRevisionFeedback(prev => ({
                                    ...prev,
                                    [order.id]: prev[order.id] ? `${prev[order.id]} ${suggestion}` : suggestion
                                  }));
                                }}
                                className="text-xs px-2 py-1 bg-zinc-700/50 hover:bg-cyan-500/20 text-zinc-400 hover:text-cyan-400 rounded transition-colors"
                              >
                                + {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Textarea
                            placeholder="Describe what changes you'd like..."
                            value={revisionFeedback[order.id] || ''}
                            onChange={(e) => setRevisionFeedback(prev => ({ ...prev, [order.id]: e.target.value }))}
                            className="bg-zinc-800 border-white/10 text-white text-sm min-h-[60px] flex-1"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestRevision(order.id);
                            }}
                            disabled={requestingRevision === order.id || !revisionFeedback[order.id]?.trim()}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white"
                          >
                            {requestingRevision === order.id ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Send className="w-3 h-3 mr-1" />
                                Submit Revision Request
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {(order.revision_count || 0) >= 2 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs text-zinc-500 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Maximum free revisions reached. For additional changes, please place a new order.
                        </p>
                      </div>
                    )}

                    {/* Chat Interface */}
                    <AnimatePresence>
                      {chatOpen === order.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-white/10"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-white flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-teal-400" />
                              Chat with {order.agent_name}
                            </h4>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setChatOpen(null)}
                              className="text-zinc-400 hover:text-white"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <ChatInterface
                            agentId={order.agent_id}
                            agentName={order.agent_name}
                            orderId={order.id}
                            sessionId={`order-${order.id}`}
                            token={token}
                            onMessageSent={(data) => console.log('Message sent:', data)}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Canvas Preview Modal */}
      <AnimatePresence>
        {canvasContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setCanvasContent(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`${canvasFullscreen ? 'w-full h-full' : 'w-full max-w-4xl max-h-[80vh]'} overflow-hidden`}
              onClick={(e) => e.stopPropagation()}
            >
              <CanvasPreview
                content={canvasContent.content}
                type={canvasContent.type || 'auto'}
                title={canvasContent.title || 'Preview'}
                onClose={() => setCanvasContent(null)}
                isFullscreen={canvasFullscreen}
                onToggleFullscreen={() => setCanvasFullscreen(!canvasFullscreen)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
